import { blockedEmailDomains } from '../config/appConfig';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailFormat(email: string): boolean {
    return EMAIL_RE.test(email.trim());
}

export function getEmailDomain(email: string): string {
    return email.trim().toLowerCase().split('@')[1] ?? '';
}

/**
 * Enforce corporate-domain sign-up: reject well-known personal email domains.
 * Returns an error message string, or null if the email is acceptable.
 */
export function validateCorporateEmail(email: string): string | null {
    if (!isValidEmailFormat(email)) {
        return 'Enter a valid email address.';
    }
    const domain = getEmailDomain(email);
    if (blockedEmailDomains.includes(domain)) {
        return 'Use your company email address. Personal email domains are not allowed.';
    }
    return null;
}

// E.164: an optional leading '+' then 1–15 digits. Cognito requires phone
// numbers in this format for the standard `phone_number` attribute.
const E164_RE = /^\+?[1-9]\d{1,14}$/;

/**
 * Validate an optional phone number. Empty is allowed (the field is optional);
 * a non-empty value must be a plausible E.164 number (digits, optional leading
 * '+', spaces/dashes/parentheses ignored). Returns an error message or null.
 */
export function validatePhone(phone: string): string | null {
    const trimmed = phone.trim();
    if (trimmed === '') return null;
    const compact = trimmed.replace(/[\s()-]/g, '');
    if (!E164_RE.test(compact)) {
        return 'Enter a valid phone number (digits only, optional leading +).';
    }
    return null;
}

/** Normalize a phone number to E.164 for storage (empty stays empty). */
export function normalizePhone(phone: string): string {
    const compact = phone.trim().replace(/[\s()-]/g, '');
    if (compact === '') return '';
    return compact.startsWith('+') ? compact : `+${compact}`;
}

/**
 * Basic password strength guard for the prototype. Cognito enforces its own
 * policy server-side; this gives fast client-side feedback.
 */
export function validatePassword(password: string): string | null {
    if (password.length < 8) {
        return 'Password must be at least 8 characters.';
    }
    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        return 'Password must include upper and lower case letters and a number.';
    }
    return null;
}
